import { useState } from 'react';
import './ColorRadioGroup.scss';
import { Modal, Button, Radio, RadioChangeEvent, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import StatusLabel, { StatusComponentType } from '../UI/StatusLabel';
import { defaultTaskStatuses, TaskStatus, TaskStatusGroup, TaskStatusGroupUtils } from '../../types/TaskStatus';
import { StatusColor } from '../../constants/design_system/statusColors';
import { EditableInput } from '../UI/EditableInput';
import {
  addTaskStatus,
  deleteTaskStatus,
  setDefaultTaskStatus,
  updateTaskStatus,
  updateTaskStatusesOrder,
} from '../../api/workspace';
import { useGetActualSpaceId, useGetWorkspaceById } from '../../serverState/workspace';
import { queryClient } from '../../queryClient';
import ReplacingStatusDropdown from '../UI/ReplacingStatusDropdown';
import Task from '../../types/Task';
import { useGetTasks } from '../../serverState/tasks';
import Workspace from '../../types/Workspace';
import { flushSync } from 'react-dom';
import { TaskStatusesGroup } from './components/TaskStatusesGroup';
import { TaskStatusGroupSelect } from './components/TaskStatusGroupSelect';
import { BaseButton } from '../UI/BaseButton';
import { FlagDefault } from '../../assets/CustomIcons/FlagDefault';
import { capitalizeFirstLetter } from '../../common/utils';
import { groupBy } from '../../utils/arrayUtils';

interface EditStatusesModalContentProps {
  /** Manage statuses for this space instead of the active route workspace (e.g. All Tasks cross-space view). */
  workspaceId?: number;
}

/**
 * Status-management UI, extracted from `EditStatusesModal` so the same logic renders
 * both inside the standalone modal and the Space Settings "Statuses" tab (#1120).
 * Contains no outer modal chrome — the caller provides it (or a settings panel).
 */
export const EditStatusesModalContent = ({ workspaceId }: EditStatusesModalContentProps) => {
  const activeSpaceId = useGetActualSpaceId();
  const effectiveSpaceId = workspaceId ?? (activeSpaceId ? Number(activeSpaceId) : null);
  const workspaceQuery = useGetWorkspaceById(effectiveSpaceId);
  const statusOptions: TaskStatus[] = (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses;
  const tasksQuery = useGetTasks(Number(workspaceQuery.data?.id));

  const [statuses, setStatuses] = useState<TaskStatus[]>(statusOptions);
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus>(
    statuses.find((status) => status.group === TaskStatusGroup.notStarted) || statuses[0]
  );
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deletionReplacingStatus, setDeletionReplacingStatus] = useState<TaskStatus | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const statusHasTasks = tasksQuery.data
    ? tasksQuery.data.filter((task) => task.statusId === selectedStatus.id).length > 0
    : true;
  const [overridenStatusHasTasks, setOverridenStatusHasTasks] = useState<boolean | null>(null);

  const groupedOptions = groupBy(statuses, 'group');

  const addStatus = async (group: TaskStatusGroup) => {
    const minOrderIndex = Math.min(
      ...statuses.filter((status) => status.group === group).map((status) => status.orderIndex ?? 0)
    );

    const newStatus: Partial<TaskStatus> = {
      group: group,
      title: generateDefaultName(),
      color: TaskStatusGroupUtils.getColor(group),
      default: false,
      orderIndex: minOrderIndex - 1,
    };

    try {
      const createdStatusResponse = await addTaskStatus(Number(effectiveSpaceId), newStatus);
      const updatedStatuses = [...statuses, createdStatusResponse.data as TaskStatus].sort(
        (a: TaskStatus, b: TaskStatus) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
      );

      setStatuses(updatedStatuses);
      setSelectedStatus(createdStatusResponse.data);

      // Update space
      queryClient.setQueryData(['workspace', Number(effectiveSpaceId)], (oldData: Workspace) => {
        const newData = oldData;
        newData.TaskStatus = updatedStatuses;
        return newData;
      });

      // Focus input of new status
      setTimeout(() => {
        flushSync(() => {
          (document.querySelector(`[title-id='${newStatus.title}']`) as HTMLElement)?.click();
        });
        const newStatusInput = document.querySelector(`[input-id='${newStatus.title}']`) as HTMLInputElement;
        newStatusInput?.focus();
        newStatusInput?.select();
      }, 200);
    } catch (error) {
      void messageApi.error(error.response.data.message || 'Failed to create task status');
    }
  };

  const generateDefaultName = () => {
    const baseName = 'New Status';
    let newName = baseName;
    let counter = 1;

    const titles = statuses.map((status) => status.title);

    while (titles.includes(newName)) {
      counter += 1;
      newName = `${baseName} ${counter}`;
    }

    return newName;
  };

  const selectStatus = (status: TaskStatus) => {
    setSelectedStatus(status);
    setOverridenStatusHasTasks(null);
  };

  const showDeleteModal = async () => {
    if (groupedOptions[selectedStatus.group].length === 1) {
      void messageApi.error('Can not delete last status in the group.');
      return;
    }
    setIsDeleteModalVisible(true);
  };

  const deleteStatus = async () => {
    const replacingStatus = deletionReplacingStatus || statusOptions.find((option) => option.default);
    if (statusHasTasks && !replacingStatus) {
      void messageApi.error('Select replacing task status.');
      return;
    }

    try {
      await deleteTaskStatus(Number(effectiveSpaceId), selectedStatus.id, replacingStatus?.id);
    } catch (error) {
      void messageApi.error(error.response.data.message);
      setOverridenStatusHasTasks(true);
      return;
    }

    const updatedStatuses = statuses.filter((status) => status !== selectedStatus);

    setStatuses(updatedStatuses);
    setSelectedStatus(
      updatedStatuses.findLast((status) => status.group === selectedStatus.group) || updatedStatuses[0]
    );

    queryClient.setQueryData(['workspace', Number(effectiveSpaceId)], (oldData: Workspace) => {
      const newData = oldData;
      newData.TaskStatus = updatedStatuses;
      return newData;
    });

    if (deletionReplacingStatus) {
      queryClient.setQueryData(['tasks', Number(effectiveSpaceId)], (oldData: Task[]) => {
        const newTasksData = oldData.map((task) => {
          if (task.statusId === selectedStatus.id) {
            return {
              ...task,
              statusId: deletionReplacingStatus.id,
              status: deletionReplacingStatus.title,
            };
          }
          return task;
        });

        return newTasksData;
      });
    }

    setIsDeleteModalVisible(false);
  };

  const onStatusChange = async (status: string) => {
    if (selectedStatus.title === status) {
      return;
    }
    await updateStatus({ title: status });
  };

  const onColorChange = async ({ target: { value } }: RadioChangeEvent) => {
    await updateStatus({ color: value });
  };

  const onGroupChange = async (group: TaskStatusGroup) => {
    if (selectedStatus.group === group) {
      return;
    }
    if (statuses.filter((status) => status.group === selectedStatus.group).length === 1) {
      void messageApi.error('Can not change the group for the last status in the group.');
      return;
    }

    const maxOrderIndex = Math.max(
      ...statuses.filter((status) => status.group === group).map((status) => status.orderIndex ?? 0)
    );
    await updateStatus({ group: group, orderIndex: maxOrderIndex + 1 });
    void messageApi.success('Status group changed.');
  };

  const onSetDefault = async () => {
    if (selectedStatus.default) {
      return;
    }
    await setDefaultTaskStatus(Number(effectiveSpaceId), selectedStatus.id);
    const updatedSelectedStatus = { ...selectedStatus, default: true };
    const updatedStatuses = statuses.map((status) => {
      if (status.default) {
        return { ...status, default: false };
      }
      return status === selectedStatus ? updatedSelectedStatus : status;
    });
    refreshStatuses(updatedStatuses, updatedSelectedStatus);
  };

  const updateStatus = async (updatedFields: any) => {
    const oldStatuses = statuses;
    const oldSelectedStatus = selectedStatus;

    const updatedSelectedStatus: TaskStatus = {
      ...selectedStatus,
      ...updatedFields,
    };
    const updatedStatuses = statuses.map((status) => (status === selectedStatus ? updatedSelectedStatus : status));
    refreshStatuses(updatedStatuses, updatedSelectedStatus);

    try {
      await updateTaskStatus(Number(effectiveSpaceId), updatedSelectedStatus);
    } catch (error) {
      // Something went wrong - fallback to previous state
      refreshStatuses(oldStatuses, oldSelectedStatus);
      void messageApi.error(error.response.data.message);
      return;
    }
  };

  const updateStatuses = async (newStatuses: TaskStatus[]) => {
    let updatedSelectedStatus = selectedStatus;
    const updatedStatuses = statuses.map((status) => {
      const updatedStatus = newStatuses.find((s) => s.id === status.id);
      if (updatedStatus && updatedStatus.id === selectedStatus.id) {
        updatedSelectedStatus = updatedStatus;
      }
      return updatedStatus ?? status;
    });

    refreshStatuses(updatedStatuses, updatedSelectedStatus);

    try {
      const statusOrderMapping: Record<number, number> = updatedStatuses.reduce(
        (acc, status) => {
          acc[status.id] = status.orderIndex ?? 0;
          return acc;
        },
        {} as Record<number, number>
      );
      await updateTaskStatusesOrder(Number(effectiveSpaceId), statusOrderMapping);
    } catch (error) {
      void messageApi.error(error.response.data.message);
      return;
    }
  };

  const refreshStatuses = (updatedStatuses: TaskStatus[], updatedSelectedStatus: TaskStatus) => {
    setStatuses(updatedStatuses);
    setSelectedStatus(updatedSelectedStatus);

    queryClient.setQueryData(['workspace', Number(effectiveSpaceId)], (oldData: Workspace) => {
      const newData = oldData;
      newData.TaskStatus = updatedStatuses;
      return newData;
    });

    queryClient.setQueryData(['tasks', Number(effectiveSpaceId)], (oldData: Task[]) => {
      const newTasksData = oldData.map((task) => {
        if (task.statusId === selectedStatus.id) {
          return {
            ...task,
            status: selectedStatus.title,
          };
        }
        return task;
      });

      return newTasksData;
    });
  };

  const handleDeleteModalCancel = () => {
    setIsDeleteModalVisible(false);
  };

  const customDeleteModalTitle = (
    <div className="h-6 justify-start items-center gap-2 inline-flex">
      <div className="text-brand-grey-dark text-base font-semibold font-['Figtree'] leading-normal">Delete</div>
      <StatusLabel
        group={selectedStatus.group}
        title={selectedStatus.title}
        color={selectedStatus.color}
        component={StatusComponentType.EditStatusesModal}
      />
      <div className="text-brand-grey-dark text-base font-semibold font-['Figtree'] leading-normal">status?</div>
    </div>
  );

  const customDeleteModalFooter = (
    <div className="h-8 pr-3 py-3 bg-brand-white rounded-bl-xl rounded-br-xl border-l border-r border-b border-neutral-300 justify-end items-start gap-3 inline-flex">
      <div
        onClick={handleDeleteModalCancel}
        className="px-3 py-2.5 bg-brand-white rounded-lg shadow border border-neutral-300 justify-end items-center gap-2 inline-flex cursor-pointer"
      >
        <div className="text-neutral-900 text-base font-semibold font-['Figtree'] leading-tight">Cancel</div>
      </div>
      <div
        onClick={deleteStatus}
        className="px-3 py-2.5 bg-[#e34935] rounded-lg shadow justify-end items-center gap-2 inline-flex cursor-pointer"
      >
        <div className="text-white text-base font-semibold font-['Figtree'] leading-tight">
          {statusHasTasks ? 'Delete Status and Reassign' : 'Delete Status'}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {contextHolder}
      <div className="h-[420px] flex">
        <div className="overflow-y-auto pr-4" style={{ flex: 1 }}>
          {Object.values(TaskStatusGroup).map((group) => (
            <div key={group} className="group">
              <div className="w-full h-6 pb-0.5 relative flex items-center">
                <div className="ml-1 text-neutral-900 text-xs font-semibold leading-none">
                  {TaskStatusGroupUtils.getTitle(group)}
                </div>
                <Button
                  onClick={() => addStatus(group)}
                  className="h-6 w-6 p-0 rounded border flex items-center justify-center ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-200 group/button"
                >
                  <PlusOutlined className="w-4 h-4 text-neutral-900 group-hover/button:text-brand-taskonio-green" />
                </Button>
              </div>
              <TaskStatusesGroup
                statuses={groupedOptions[group]}
                selectedStatus={selectedStatus}
                selectStatus={selectStatus}
                updateStatuses={updateStatuses}
              />
            </div>
          ))}
        </div>
        <div className="w-px min-h-full bg-neutral-300 rounded-sm" />
        <div className="mx-4 flex flex-col" style={{ flex: 1.4 }}>
          <>
            <EditableInput value={selectedStatus.title} onValueUpdated={onStatusChange} />
            <TaskStatusGroupSelect
              statusGroup={selectedStatus.group}
              selectStatusGroup={(group: TaskStatusGroup) => onGroupChange(group)}
            />
            <div className="text-neutral-900 text-xs font-semibold leading-none mt-2">Color</div>
            {/* Checked colour comes from ColorRadioGroup.scss, not an AntD theme
                token — see that file for why the token could not work. #2639 */}
            <Radio.Group
              className="color-radio-group grid grid-cols-2 gap-1 mt-1 mr-16"
              value={selectedStatus.color}
              onChange={onColorChange}
            >
              {Object.values(StatusColor).map((color) => (
                <Radio className="py-1" value={color} key={color}>
                  <StatusLabel
                    color={color}
                    title={capitalizeFirstLetter(color)}
                    group={TaskStatusGroup.notStarted}
                    component={StatusComponentType.EditStatusesModalColor}
                  />
                </Radio>
              ))}
            </Radio.Group>
            <div className="flex justify-between mt-auto">
              <BaseButton
                type="secondary"
                size="medium"
                disabled={selectedStatus.default}
                danger={true}
                onClick={showDeleteModal}
                disabledTooltip="Default status can not be deleted"
              >
                <DeleteOutlined />
                Delete
              </BaseButton>

              <BaseButton
                type="secondary"
                size="medium"
                disabled={selectedStatus.default}
                onClick={onSetDefault}
                disabledTooltip="This status is already default"
              >
                <FlagDefault />
                Set as Default
              </BaseButton>
            </div>
          </>
        </div>
      </div>

      <Modal
        title={customDeleteModalTitle}
        open={isDeleteModalVisible}
        onCancel={handleDeleteModalCancel}
        zIndex={2000}
        width={640}
        footer={customDeleteModalFooter}
        centered
      >
        {(overridenStatusHasTasks || statusHasTasks) && (
          <>
            <div className="w-[512px] text-brand-grey-dark text-sm font-normal font-['Figtree'] leading-tight mb-4 mt-4">
              Deleting this status will remove it from all tasks in {workspaceQuery.data?.name}.<br />
              <br />
              Which status would you like to assign those tasks to instead?
            </div>
            <ReplacingStatusDropdown
              filterStatus={selectedStatus}
              onSelect={(e) => {
                setDeletionReplacingStatus(statuses.find((status) => status.id === Number(e)));
              }}
              onEditStatuses={() => {}}
              onBlur={() => {}}
            />
          </>
        )}
        {!(overridenStatusHasTasks || statusHasTasks) && (
          <div className="w-[512px] text-brand-grey-dark text-sm font-normal font-['Figtree'] leading-tight mb-4 mt-4">
            Upon deleting this status, you will no longer be able to assign it to any task.
          </div>
        )}
      </Modal>
    </>
  );
};
